/**
 * 고객 '월 별 내역서' 이용월 풀다운 유틸 (2026-10-15).
 *
 * ## 왜 생겼나
 *
 * 종전 화면은 연도(2024~올해)·월(1~12)을 **따로** 고르게 했고, 어느 달을 골라도 서버가 값을 만들어
 * 보여 줬다. 그래서
 *  - 9월에 가입한 고객이 7월 이하로 내려가면 월 비용 33,000원짜리 **유령 내역서**가 떴고
 *    (legacy `/cash/bill` 이 가입일과 무관하게 `monthly_price × store_count` 를 무조건 찍는다),
 *  - 지금 10월인데 11·12월이 보이고, 서비스와 무관한 2024년도 골라졌다.
 *
 * 어느 달이 의미 있는지는 채널마다 다르고 그 근거(가입일·구독·정산서)는 전부 서버에 있다. 그래서
 * **서버가 내려준 목록(`GET /cash/invoice/months`)으로만** 풀다운을 채우고, 연·월을 합친 한 칸으로 고른다.
 * 프론트는 목록 밖의 달을 절대 요청하지 않는다.
 */

/** 'YYYY-MM' → '2026년 9월'. 형식이 깨진 값은 그대로 돌려준다(빈 칸보다 낫다). */
export const monthText = (month) => {
  const m = /^(\d{4})-(\d{2})$/.exec(month ?? '');
  if (!m) return month ?? '';
  return `${Number(m[1])}년 ${Number(m[2])}월`;
};

/**
 * 풀다운 한 줄 문구. 확정 전 달은 **고르기 전에** 알 수 있게 '(작성중)' 을 붙인다 — 골라 보고 나서야
 * "정산서 작성중입니다" 를 만나면 다른 달도 그런지 하나씩 눌러 봐야 한다.
 *
 * @param {{month: string, status: string}} entry 서버 목록 항목
 */
export const monthOptionLabel = (entry) => {
  const base = monthText(entry?.month);
  return entry?.status === 'PENDING' ? `${base} (작성중)` : base;
};

/** 그 달의 마지막 날짜 'DD'. (2월·윤년 포함 — Date 의 0일 트릭) */
const lastDayOf = (year, month) => String(new Date(year, month, 0).getDate()).padStart(2, '0');

/**
 * legacy `/cash/bill` 호출 인자. `startDate` = 'YYYY-MM-01', `endDate` = 그 달 말일.
 * (서버는 startDate 만 보고 월을 정하지만, 종전 호출과 같은 모양을 유지한다.)
 */
export const monthRange = (month) => {
  const m = /^(\d{4})-(\d{2})$/.exec(month ?? '');
  if (!m) return null;
  const [year, mon] = [Number(m[1]), Number(m[2])];
  return { startDate: `${month}-01`, endDate: `${month}-${lastDayOf(year, mon)}` };
};

/** 상단 '기간' 칸 문구. 종전 표기(`2026.9.01 ~ 2026 9.30`)의 띄어쓰기 오류를 바로잡았다. */
export const periodText = (month) => {
  const m = /^(\d{4})-(\d{2})$/.exec(month ?? '');
  if (!m) return '';
  const [year, mon] = [Number(m[1]), Number(m[2])];
  return `${year}.${mon}.01 ~ ${year}.${mon}.${lastDayOf(year, mon)}`;
};

/**
 * 목록이 바뀌었을 때 유지할 선택값. 지금 고른 달이 새 목록에 있으면 그대로, 없으면 **가장 최근 달**.
 * (채널 전환기로 채널을 바꾸면 목록이 통째로 달라진다 — 앞 채널의 달이 남아 있으면 안 된다.)
 */
export const pickMonth = (months, current) => {
  if (!Array.isArray(months) || months.length === 0) return null;
  if (current && months.some((e) => e.month === current)) return current;
  return months[0].month;
};
